// Obsidian-style "live preview" for CodeMirror 6: hides Markdown syntax on lines that do not
// contain the cursor and renders task lines (checkboxes + token chips).
import { foldEffect, foldedRanges, syntaxTree, unfoldEffect } from "@codemirror/language";
import { type EditorState, type Extension, type Range, RangeSet, StateEffect } from "@codemirror/state";
import {
  Decoration,
  type DecorationSet,
  EditorView,
  ViewPlugin,
  type ViewUpdate,
  WidgetType,
} from "@codemirror/view";
import type { SyntaxNodeRef } from "@lezer/common";
import { actorHandle, humanDate } from "../lib/format";
import { richAnchorCursor } from "./anchors";
import { editorContext } from "./context";
import { taskBlocksOf, taskFoldRange, visualX, type VisualX } from "./taskBlocks";
import { isOverdue, parseTaskLine, resolveDate, type Token } from "./tasktokens";

/** Dispatch to force a re-render of Rich decorations (e.g. after anchors were resolved). */
export const refreshDecorations = StateEffect.define<null>();

// ---------------------------------------------------------------------------
// Widgets

class CheckboxWidget extends WidgetType {
  constructor(
    readonly checked: boolean,
    readonly withBullet: boolean,
  ) {
    super();
  }
  eq(other: CheckboxWidget) {
    return other.checked === this.checked && other.withBullet === this.withBullet;
  }
  toDOM(view: EditorView) {
    const wrap = document.createElement("span");
    wrap.className = "cm-lp-task";
    const box = document.createElement("input");
    box.type = "checkbox";
    box.checked = this.checked;
    box.className = "cm-lp-checkbox";
    box.setAttribute("aria-label", this.checked ? "Mark task as open" : "Mark task as done");
    box.addEventListener("mousedown", (e) => e.preventDefault());
    box.addEventListener("click", (e) => {
      e.preventDefault();
      toggleAt(view, view.posAtDOM(wrap));
    });
    box.addEventListener("keydown", (e) => {
      if (e.key === " " || e.key === "Enter") {
        e.preventDefault();
        toggleAt(view, view.posAtDOM(wrap));
      }
    });
    wrap.appendChild(box);
    return wrap;
  }
  ignoreEvent() {
    return true;
  }
}

function toggleAt(view: EditorView, pos: number): void {
  const line = view.state.doc.lineAt(pos);
  const task = parseTaskLine(line.text);
  if (!task) return;
  const from = line.from + task.boxFrom + 1;
  view.dispatch({
    changes: { from, to: from + 1, insert: task.checked ? " " : "x" },
    userEvent: "input.toggle",
  });
}

class BulletWidget extends WidgetType {
  eq() {
    return true;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = "cm-lp-bullet";
    s.textContent = "•";
    s.setAttribute("aria-hidden", "true");
    return s;
  }
}

class HrWidget extends WidgetType {
  eq() {
    return true;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = "cm-lp-hr";
    return s;
  }
}

class ChipWidget extends WidgetType {
  constructor(
    readonly cls: string,
    readonly text: string,
    readonly title: string,
  ) {
    super();
  }
  eq(o: ChipWidget) {
    return o.cls === this.cls && o.text === this.text && o.title === this.title;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = `cm-chip ${this.cls}`;
    s.textContent = this.text;
    if (this.title) s.title = this.title;
    return s;
  }
  ignoreEvent() {
    return false;
  }
}

const cssX = (v: VisualX) => `calc(${+v.em.toFixed(3)}em + ${v.px}px)`;

/** Fixed-width replacement for the indentation of lines inside task blocks. */
class SpacerWidget extends WidgetType {
  constructor(readonly x: VisualX) {
    super();
  }
  eq(o: SpacerWidget) {
    return o.x.em === this.x.em && o.x.px === this.x.px;
  }
  toDOM() {
    const s = document.createElement("span");
    s.className = "cm-lp-indent";
    s.style.width = cssX(this.x);
    s.setAttribute("aria-hidden", "true");
    return s;
  }
  ignoreEvent() {
    return false;
  }
}

/** Fold / unfold chevron in front of a task line that has a block. */
class FoldChevronWidget extends WidgetType {
  constructor(readonly folded: boolean) {
    super();
  }
  eq(o: FoldChevronWidget) {
    return o.folded === this.folded;
  }
  toDOM(view: EditorView) {
    const wrap = document.createElement("span");
    wrap.className = "cm-lp-fold";
    const b = document.createElement("button");
    b.type = "button";
    b.className = `cm-lp-fold-btn${this.folded ? " folded" : ""}`;
    b.textContent = this.folded ? "▸" : "▾";
    b.title = this.folded ? "Show task notes & subtasks" : "Hide task notes & subtasks";
    b.setAttribute("aria-label", b.title);
    b.setAttribute("aria-expanded", String(!this.folded));
    b.tabIndex = -1; // keyboard: Ctrl-Shift-[ / ] (fold keymap)
    b.addEventListener("mousedown", (e) => e.preventDefault());
    b.addEventListener("click", (e) => {
      e.preventDefault();
      const pos = view.posAtDOM(wrap);
      const range = taskFoldRange(view.state, view.state.doc.lineAt(pos).from);
      if (!range) return;
      let folded = false;
      foldedRanges(view.state).between(range.from, range.from + 1, (f) => {
        if (f === range.from) folded = true;
      });
      view.dispatch({ effects: (folded ? unfoldEffect : foldEffect).of(range) });
    });
    wrap.appendChild(b);
    return wrap;
  }
  ignoreEvent() {
    return true;
  }
}

/** Small "open task" link at the end of anchored task lines. */
class OpenTaskWidget extends WidgetType {
  constructor(readonly itemId: string) {
    super();
  }
  eq(o: OpenTaskWidget) {
    return o.itemId === this.itemId;
  }
  toDOM(view: EditorView) {
    const b = document.createElement("button");
    b.type = "button";
    b.className = "cm-lp-open";
    b.textContent = "↗";
    b.title = "Open task";
    b.setAttribute("aria-label", "Open task");
    b.addEventListener("mousedown", (e) => e.preventDefault());
    b.addEventListener("click", (e) => {
      e.preventDefault();
      view.state.facet(editorContext)().openItem?.(this.itemId);
    });
    return b;
  }
  ignoreEvent() {
    return true;
  }
}

const PRIORITY_NAMES: Record<string, string> = { A: "urgent", B: "high", C: "normal" };

function tokenChip(tok: Token, view: EditorView, checked: boolean): ChipWidget | null {
  const ctx = view.state.facet(editorContext)();
  const now = ctx.now ? ctx.now() : new Date();
  switch (tok.kind) {
    case "priority": {
      const name = PRIORITY_NAMES[tok.value] ?? "low";
      return new ChipWidget(`cm-chip-prio cm-prio-${name}`, "", `Priority (${tok.value}) – ${name}`);
    }
    case "due":
    case "start": {
      const r = resolveDate(tok.value, now);
      const label = tok.kind === "due" ? "due" : "start";
      if (!r) return new ChipWidget("cm-chip-date cm-chip-invalid", `${label} ${tok.value}`, "Unrecognized date");
      const overdue = tok.kind === "due" && !checked && isOverdue(tok.value, now);
      const text = `${humanDate(r.date, now)}${r.time ? ` ${r.time}` : ""}`;
      return new ChipWidget(
        `cm-chip-date cm-chip-${label}${overdue ? " cm-chip-overdue" : ""}`,
        text,
        `${label === "due" ? "Due" : "Start"} ${r.date}${r.time ? ` ${r.time}` : ""}${overdue ? " (overdue)" : ""}`,
      );
    }
    case "person": {
      const actor = ctx.members.find((a) => actorHandle(a) === tok.value.toLowerCase());
      return new ChipWidget("cm-chip-person", `@${actor ? actor.display_name : tok.value}`, tok.text);
    }
    case "label":
      return new ChipWidget("cm-chip-label", `#${tok.value}`, "Label");
    case "project":
      return new ChipWidget("cm-chip-project", `+${tok.value}`, "Project / collection");
    case "status":
      return new ChipWidget("cm-chip-field", `status: ${tok.value}`, "Status");
    case "bucket":
      return new ChipWidget("cm-chip-bucket", tok.value.replace(/-/g, " "), "Bucket");
    case "recurrence":
      return new ChipWidget("cm-chip-field", `↻ ${tok.value}`, "Recurrence");
    case "field":
      return new ChipWidget("cm-chip-field", `${tok.key}: ${tok.value}`, "Custom field");
    case "completion":
      return new ChipWidget("cm-chip-done", `✓ ${humanDate(tok.value, now)}`, `Completed ${tok.value}`);
    case "link":
      return new ChipWidget("cm-chip-link", tok.value, "Linked item");
    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// Decoration builder

const hide = Decoration.replace({});
const lineClass = (cls: string) => Decoration.line({ class: cls });
const mark = (cls: string) => Decoration.mark({ class: cls });

interface Built {
  decorations: DecorationSet;
  atomic: DecorationSet;
}

function activeLines(view: EditorView): Set<number> {
  const set = new Set<number>();
  if (!view.hasFocus) return set;
  for (const r of view.state.selection.ranges) {
    const a = view.state.doc.lineAt(r.from).number;
    const b = view.state.doc.lineAt(r.to).number;
    for (let i = a; i <= b; i++) set.add(i);
  }
  return set;
}

function isInsideCode(state: EditorState, pos: number): boolean {
  let node: SyntaxNodeRef["node"] | null = syntaxTree(state).resolveInner(pos, 1);
  for (; node; node = node.parent) {
    if (node.name === "FencedCode" || node.name === "CodeBlock" || node.name === "InlineCode") return true;
  }
  return false;
}

function build(view: EditorView): Built {
  const { state } = view;
  const doc = state.doc;
  const active = activeLines(view);
  const decos: Range<Decoration>[] = [];
  const atomic: Range<Decoration>[] = [];
  const handledTaskLines = new Set<number>();
  const isActive = (pos: number) => active.has(doc.lineAt(pos).number);

  /** Task line: checkbox + chips, plus inline emphasis/code/strike markers hidden like elsewhere. */
  const taskLine = (line: { from: number; to: number; text: string }, lineActive: boolean) => {
    const replaced = decorateTask(view, line.from, line.text, lineActive, decos, atomic);
    const overlaps = (f: number, t: number) => replaced.some(([a, b]) => f < b && t > a);
    syntaxTree(state).iterate({
      from: line.from,
      to: line.to,
      enter: (n) => {
        if (n.to <= line.from || n.from >= line.to) return;
        if (n.name === "InlineCode") {
          if (!overlaps(n.from, n.to)) decos.push(mark("cm-lp-code").range(n.from, n.to));
          return;
        }
        if (n.name === "Strikethrough") {
          if (!overlaps(n.from, n.to)) decos.push(mark("cm-lp-strike").range(n.from, n.to));
          return;
        }
        if (n.name === "EmphasisMark" || n.name === "StrikethroughMark" || n.name === "CodeMark") {
          if (n.node.parent?.name === "FencedCode") return;
          if (!lineActive && !overlaps(n.from, n.to)) hideRange(n.from, n.to);
        }
      },
    });
  };

  const hideRange = (from: number, to: number) => {
    if (to > from) {
      decos.push(hide.range(from, to));
      atomic.push(hide.range(from, to));
    }
  };

  for (const { from, to } of view.visibleRanges) {
    syntaxTree(state).iterate({
      from,
      to,
      enter: (node) => {
        const name = node.name;
        const lineActive = isActive(node.from);

        if (/^ATXHeading[1-6]$/.test(name)) {
          const level = name.slice(-1);
          decos.push(lineClass(`cm-lp-h cm-lp-h${level}`).range(doc.lineAt(node.from).from));
          return;
        }
        if (/^SetextHeading[12]$/.test(name)) {
          decos.push(lineClass(`cm-lp-h cm-lp-h${name.slice(-1)}`).range(doc.lineAt(node.from).from));
          return;
        }
        if (name === "HeaderMark") {
          if (!lineActive) {
            const line = doc.lineAt(node.from);
            // ATX: hide "## " (including trailing space); Setext underline: hide the line content.
            let end = node.to;
            if (end < line.to && doc.sliceString(end, end + 1) === " ") end++;
            hideRange(node.from, end);
          }
          return;
        }
        if (name === "EmphasisMark" || name === "CodeMark" || name === "StrikethroughMark") {
          // Fenced code markers are also CodeMark: only hide inline ones.
          const parent = node.node.parent?.name;
          if (parent === "FencedCode") return;
          if (!lineActive) hideRange(node.from, node.to);
          return;
        }
        if (name === "InlineCode") {
          decos.push(mark("cm-lp-code").range(node.from, node.to));
          return;
        }
        if (name === "Strikethrough") {
          decos.push(mark("cm-lp-strike").range(node.from, node.to));
          return;
        }
        if (name === "Link") {
          if (lineActive) return;
          const n = node.node;
          const marks = n.getChildren("LinkMark");
          const url = n.getChild("URL");
          if (marks.length >= 2) {
            // [text](url "title"): hide "[" and everything from "]" to the end.
            const open = marks[0];
            const close = marks[1];
            hideRange(open.from, open.to);
            decos.push(
              Decoration.mark({
                class: "cm-lp-link",
                attributes: url ? { title: doc.sliceString(url.from, url.to) } : {},
              }).range(open.to, close.from),
            );
            hideRange(close.from, n.to);
          }
          return false;
        }
        if (name === "FencedCode") {
          for (let pos = node.from; pos <= node.to; ) {
            const line = doc.lineAt(pos);
            decos.push(lineClass("cm-lp-codeblock").range(line.from));
            pos = line.to + 1;
          }
          return false;
        }
        if (name === "Blockquote") {
          for (let pos = node.from; pos <= node.to; ) {
            const line = doc.lineAt(pos);
            decos.push(lineClass("cm-lp-quote").range(line.from));
            pos = line.to + 1;
          }
          return;
        }
        if (name === "QuoteMark") {
          if (!lineActive) {
            let end = node.to;
            if (doc.sliceString(end, end + 1) === " ") end++;
            hideRange(node.from, end);
          }
          return;
        }
        if (name === "HorizontalRule") {
          if (!lineActive) {
            decos.push(Decoration.replace({ widget: new HrWidget() }).range(node.from, node.to));
          }
          return;
        }
        if (name === "Task") {
          const line = doc.lineAt(node.from);
          if (handledTaskLines.has(line.number)) return false;
          handledTaskLines.add(line.number);
          taskLine(line, lineActive);
          return false;
        }
        if (name === "ListMark") {
          const parentItem = node.node.parent;
          if (!parentItem || parentItem.parent?.name !== "BulletList") return;
          if (parentItem.getChild("Task")) {
            // Task lines are handled as a whole.
            const line = doc.lineAt(node.from);
            if (!handledTaskLines.has(line.number) && !isInsideCode(state, node.from)) {
              handledTaskLines.add(line.number);
              taskLine(line, lineActive);
            }
            return;
          }
          if (!lineActive) {
            decos.push(Decoration.replace({ widget: new BulletWidget() }).range(node.from, node.to));
          }
          return;
        }
        return;
      },
    });
  }

  decorateBlocks(view, decos);

  return {
    decorations: Decoration.set(decos, true),
    atomic: Decoration.set(atomic, true),
  };
}

const GUIDE_OFFSET_PX = 7; // checkbox centre

/** Indent guides, fixed indentation, fold chevrons and "open task" links for task blocks. */
function decorateBlocks(view: EditorView, decos: Range<Decoration>[]): void {
  const { state } = view;
  const doc = state.doc;
  const analysis = taskBlocksOf(doc);
  const ctx = state.facet(editorContext)();
  const anchors = ctx.anchors;
  const folded = foldedRanges(state);
  const isFolded = (pos: number) => {
    let hit = false;
    folded.between(pos, pos + 1, (f) => {
      if (f === pos) hit = true;
    });
    return hit;
  };
  const hiddenByFold = (pos: number) => {
    let hit = false;
    folded.between(pos, pos, (f, t) => {
      if (f < pos && pos <= t) hit = true;
    });
    return hit;
  };
  for (const { from, to } of view.visibleRanges) {
    for (let pos = from; pos <= to; ) {
      const line = doc.lineAt(pos);
      pos = line.to + 1;
      if (hiddenByFold(line.to)) continue;
      const info = analysis.lines[line.number - 1];
      if (!info) continue;
      const enc = info.enclosing;
      if (enc.length) {
        const guides = enc.map((t) => `calc(var(--lp-line-pad, 6px) + ${cssX({ em: t.vis.em, px: t.vis.px + GUIDE_OFFSET_PX })}) 0`);
        decos.push(
          Decoration.line({
            class: `cm-lp-block cm-lp-block-d${Math.min(enc.length, 4)}`,
            attributes: {
              style:
                `background-image: ${enc.map(() => "linear-gradient(var(--lp-guide), var(--lp-guide))").join(", ")};` +
                `background-position: ${guides.join(", ")};`,
            },
          }).range(line.from),
        );
      }
      if (!info.blank && info.indent > 0 && (enc.length || info.isTask) && !isInsideCode(state, line.from + info.indent)) {
        decos.push(Decoration.replace({ widget: new SpacerWidget(visualX(info.indent, enc)) }).range(line.from, line.from + info.indent));
      }
      if (info.isTask) {
        const markerFrom = line.from + info.indent;
        if (analysis.blocks.has(line.number)) {
          decos.push(
            Decoration.widget({ widget: new FoldChevronWidget(isFolded(line.to)), side: -1 }).range(markerFrom),
          );
        }
        if (anchors && anchors.size) {
          const m = /\s\^([a-z0-9]{4,12})\s*$/.exec(line.text);
          const id = m ? anchors.get(m[1]) : undefined;
          if (id) decos.push(Decoration.widget({ widget: new OpenTaskWidget(id), side: 1 }).range(line.to));
        }
      }
    }
  }
}

function decorateTask(
  view: EditorView,
  lineFrom: number,
  text: string,
  lineActive: boolean,
  decos: Range<Decoration>[],
  atomic: Range<Decoration>[],
): [number, number][] {
  const replaced: [number, number][] = [];
  const task = parseTaskLine(text);
  if (!task) return replaced;
  const markerFrom = lineFrom + task.indent.length;
  const boxFrom = lineFrom + task.boxFrom;
  const boxTo = lineFrom + task.boxTo;
  if (task.checked) decos.push(lineClass("cm-lp-task-done").range(lineFrom));
  decos.push(lineClass("cm-lp-taskline").range(lineFrom));

  const sel = view.state.selection.main;
  const cursorInBox = view.hasFocus && sel.from <= boxTo && sel.to >= markerFrom && lineActive;
  if (!cursorInBox) {
    // Replace "- [ ]" with a checkbox (on the active line keep the bullet marker visible).
    const from = lineActive ? boxFrom : markerFrom;
    let to = boxTo;
    if (!lineActive && text[task.boxTo] === " ") to++;
    const deco = Decoration.replace({ widget: new CheckboxWidget(task.checked, !lineActive) });
    decos.push(deco.range(from, to));
    atomic.push(deco.range(from, to));
    replaced.push([from, to]);
  }

  for (const tok of task.tokens) {
    const from = lineFrom + tok.from;
    const to = lineFrom + tok.to;
    if (tok.kind === "anchor") {
      // Hide it with its one separator space; other spaces the user typed stay visible.
      const minStart = lineFrom + Math.min(task.boxTo + 1, tok.from);
      const start = from > minStart && /[ \t]/.test(text[from - lineFrom - 1]) ? from - 1 : from;
      decos.push(hide.range(start, to));
      atomic.push(hide.range(start, to));
      replaced.push([start, to]);
      continue;
    }
    if (tok.kind === "word" || tok.kind === "code") continue;
    if (lineActive) {
      decos.push(mark(`cm-tok cm-tok-${tok.kind}`).range(from, to));
      continue;
    }
    const chip = tokenChip(tok, view, task.checked);
    if (chip) {
      const deco = Decoration.replace({ widget: chip });
      decos.push(deco.range(from, to));
      atomic.push(deco.range(from, to));
      replaced.push([from, to]);
    }
  }
  return replaced;
}

const livePreviewPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    atomic: DecorationSet;
    constructor(view: EditorView) {
      const b = build(view);
      this.decorations = b.decorations;
      this.atomic = b.atomic;
    }
    update(u: ViewUpdate) {
      if (
        u.docChanged ||
        u.viewportChanged ||
        u.selectionSet ||
        u.focusChanged ||
        syntaxTree(u.startState) !== syntaxTree(u.state) ||
        foldedRanges(u.startState) !== foldedRanges(u.state) ||
        u.transactions.some((t) => t.effects.some((e) => e.is(refreshDecorations)))
      ) {
        const b = build(u.view);
        this.decorations = b.decorations;
        this.atomic = b.atomic;
      }
    }
  },
  {
    decorations: (v) => v.decorations,
    provide: (plugin) =>
      EditorView.atomicRanges.of((view) => view.plugin(plugin)?.atomic ?? RangeSet.empty),
  },
);

/** Rich mode extension: live preview decorations + a root class for styling. */
export function livePreview(): Extension {
  return [livePreviewPlugin, richAnchorCursor, EditorView.editorAttributes.of({ class: "cm-rich" })];
}

/** Source mode: syntax highlighting only, but task tokens get subtle coloring. */
const sourceTokenPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet;
    constructor(view: EditorView) {
      this.decorations = this.build(view);
    }
    update(u: ViewUpdate) {
      if (u.docChanged || u.viewportChanged || syntaxTree(u.startState) !== syntaxTree(u.state)) {
        this.decorations = this.build(u.view);
      }
    }
    build(view: EditorView): DecorationSet {
      const decos: Range<Decoration>[] = [];
      for (const { from, to } of view.visibleRanges) {
        for (let pos = from; pos <= to; ) {
          const line = view.state.doc.lineAt(pos);
          const task = parseTaskLine(line.text);
          if (task && !isInsideCode(view.state, line.from + task.boxFrom)) {
            for (const tok of task.tokens) {
              if (tok.kind === "word" || tok.kind === "code") continue;
              decos.push(mark(`cm-tok cm-tok-${tok.kind}`).range(line.from + tok.from, line.from + tok.to));
            }
          }
          pos = line.to + 1;
        }
      }
      return Decoration.set(decos, true);
    }
  },
  { decorations: (v) => v.decorations },
);

export function sourceMode(): Extension {
  return [sourceTokenPlugin, EditorView.editorAttributes.of({ class: "cm-source" })];
}
